import { createFileRoute, Link } from "@tanstack/react-router";
import { useSystemStats, systemStatsKeys } from "../../../hooks/useSystemStats";
import { getSystemStats } from "../../../server/stats";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { StatsRowSkeleton } from "../../../components/RouteSkeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";

export const Route = createFileRoute("/_admin/stats/")({
  // Phase 3 loader — pre-seeds the TanStack Query cache with stats data
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData({
      queryKey: systemStatsKeys.stats(),
      queryFn: async () => {
        const result = await getSystemStats({ data: {} });
        if (result.error) throw new Error(`Failed to load system stats: ${result.error}`);
        return result.data;
      },
      staleTime: 1000 * 60 * 5, // 5 minutes — matches useSystemStats
    });
  },
  pendingComponent: () => (
    <div className="space-y-6 p-6">
      <div className="h-8 w-48 rounded bg-muted animate-pulse" />
      <StatsRowSkeleton count={4} />
    </div>
  ),
  errorComponent: createRouteError("System Stats"),
  component: StatsPage,
});

function StatsPage() {
  const { data: stats, isLoading, error } = useSystemStats();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading system stats...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <div className="text-destructive text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Error Loading Stats
          </h2>
          <p className="text-muted-foreground mb-4">{error.message}</p>
          <Link
            to="/dashboard"
            className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      {/* Main Content */}
      <main className="container mx-auto p-6 space-y-6">
        {/* Success Message */}
        <div className="rounded-lg border bg-green-50 dark:bg-green-950 p-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl">✅</span>
            <div>
              <h2 className="font-semibold text-green-900 dark:text-green-100">
                Vertical Feature Slice Working!
              </h2>
              <p className="text-sm text-green-700 dark:text-green-300">
                This demonstrates the full TanStack Start migration pattern:
                Server Function → TanStack Query → UI Component
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={stats?.totalUsers.toLocaleString() ?? "—"}
            description="Registered users"
            icon="👥"
          />
          <StatCard
            title="Active Projects"
            value={stats?.activeProjects.toLocaleString() ?? "—"}
            description="Currently in progress"
            icon="📁"
          />
          <StatCard
            title="Completed Tasks"
            value={stats?.completedTasks.toLocaleString() ?? "—"}
            description="All-time total"
            icon="✓"
          />
          <StatCard
            title="System Uptime"
            value={stats?.uptime ?? "—"}
            description="Last 30 days"
            icon="⚡"
          />
        </div>

        {/* Architecture Explanation */}
        <Card>
          <CardHeader>
            <CardTitle>How This Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-3">
                <span className="text-2xl">1️⃣</span>
                <div>
                  <strong className="text-foreground">
                    Server Function (stats.server.ts)
                  </strong>
                  <p className="mt-1">
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      getSystemStats
                    </code>{" "}
                    runs on the server via{" "}
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      createServerFn
                    </code>
                    . It has access to server-only resources (Service Role Key,
                    API keys, etc).
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-2xl">2️⃣</span>
                <div>
                  <strong className="text-foreground">
                    TanStack Query Hook (useSystemStats.ts)
                  </strong>
                  <p className="mt-1">
                    The{" "}
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      useSystemStats
                    </code>{" "}
                    hook wraps the server function call with TanStack Query,
                    providing caching, refetching, and loading states.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-2xl">3️⃣</span>
                <div>
                  <strong className="text-foreground">
                    UI Component (This Page)
                  </strong>
                  <p className="mt-1">
                    The route component uses the hook to display data reactively.
                    No direct API calls, no legacy service classes — just modern
                    React patterns.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950 rounded-md">
              <p className="text-sm text-blue-900 dark:text-blue-100">
                <strong>Migration Pattern Established:</strong> This same pattern
                can be applied to migrate todos, clients, projects, and all other
                features from the legacy flowstate-automate app.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Metadata */}
        {stats?.lastUpdated && (
          <div className="text-center text-xs text-muted-foreground">
            Last updated: {new Date(stats.lastUpdated).toLocaleString()}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({
  title,
  value,
  description,
  icon,
}: {
  title: string;
  value: string;
  description: string;
  icon: string;
}) {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="pt-6 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <span className="text-2xl">{icon}</span>
        </div>
        <p className="text-3xl font-bold text-card-foreground">{value}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}
